/* global window._appConstants */
import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Menu from "@material-ui/core/Menu";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import Autocomplete from "@material-ui/lab/Autocomplete";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import DonutLargeIcon from "@material-ui/icons/DonutLarge";
import TuneIcon from "@material-ui/icons/Tune";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CancelOutlinedIcon from "@material-ui/icons/CancelOutlined";
import DoneAllOutlinedIcon from "@material-ui/icons/DoneAllOutlined";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import HourglassEmptyOutlinedIcon from "@material-ui/icons/HourglassEmptyOutlined";
import HighlightOffOutlinedIcon from "@material-ui/icons/HighlightOffOutlined";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import InputAdornment from "@material-ui/core/InputAdornment";
import Pagination from "@material-ui/lab/Pagination";
import moment from "moment";
import * as _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { CustomProgressBar } from "../../components";
import { OAuth as OAuthActions } from "../../js/actions";
import { Context } from "../../js/utils/Context";
import { setSnackbar } from "../../js/reducers/snackbar";
import { setAlertDialog } from "../../js/reducers/alertDialog";
import { buildApiUrl } from "../../config/environment";
import chartSvg from "../../assets/images/svg/chart-simple-solid-full-pink.svg";

// ----- Tokens -----
const ACCENT = "#1d4ed8";
const ACCENT_DARK = "#1e40af";
const SURFACE = "#ffffff";
const PAGE_BG = "#f8fafc";
const BORDER = "#e5e7eb";
const TEXT_TITLE = "#0f172a";
const TEXT_MUTED = "#64748b";

const COLORS = {
    blue100: "#dbeafe",
    blue700: "#1d4ed8",
};

const STATUS_PALETTE = {
    NEW: {
        main: "#10b981",
        soft: "#ecfdf5",
        border: "#a7f3d0",
        label: "New",
        Icon: AddCircleOutlineIcon,
        legacyIcon: DescriptionOutlinedIcon,
    },
    IN_PROGRESS: {
        main: "#4f46e5",
        soft: "#eef2ff",
        border: "#c7d2fe",
        label: "In-Progress",
        Icon: AutorenewIcon,
        legacyIcon: HourglassEmptyOutlinedIcon,
    },
    COMPLETED: {
        main: "#7c3aed",
        soft: "#f5f3ff",
        border: "#ddd6fe",
        label: "Completed",
        Icon: CheckCircleOutlineIcon,
        legacyIcon: DoneAllOutlinedIcon,
    },
    CANCELLED: {
        main: "#ef4444",
        soft: "#fef2f2",
        border: "#fecaca",
        label: "Cancelled",
        Icon: CancelOutlinedIcon,
        legacyIcon: HighlightOffOutlinedIcon,
    },
};

const useStyles = makeStyles((theme) => ({
    page: {
        flexGrow: 1,
        padding: theme.spacing(3),
        backgroundColor: PAGE_BG,
        minHeight: "100%",
        fontFamily: theme.typography.fontFamily,
    },
    stack: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1.25),
    },

    // ---------- Page header ----------
    pageHeaderLeft: {
        display: "flex",
        alignItems: "center",
        paddingBottom: theme.spacing(1.5),
    },

    moreDetailsTitle: {
        fontSize: "13px !important",
        fontWeight: "700 !important",
        color: "#1d4ed8 !important",
        textTransform: "uppercase !important",
        letterSpacing: "0.4px !important",
    },

    headerIcon: {
        width: 40,
        height: 40,
        borderRadius: 12,
        background: "linear-gradient(135deg, #1a56db 0%, #1e40af 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginRight: 12,
    },

    pageIconBox: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: COLORS.blue100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginRight: 12,
    },

    pageTitleIcon: {
        fontSize: "22px !important",
        color: COLORS.blue700,
    },
    headerActions: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.75),
    },

    overviewBody: {
        padding: theme.spacing(1.5),
    },
    bentoGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: "auto auto auto",
        gridTemplateAreas: `
      "hero hero"
      "new inprog"
      "comp cancel"
    `,
        gap: theme.spacing(1.7),
    },

    // Hero card (Total Jobs)
    bentoHero: {
        gridArea: "hero",
        position: "relative",
        overflow: "hidden",
        padding: theme.spacing(1.75, 2),
        borderRadius: 14,
        background:
            "linear-gradient(135deg, #1e293b 0%, #312e81 55%, #4338ca 100%)",
        color: "white",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        minHeight: 96,
        flexWrap: "wrap",
        "&::before": {
            content: '""',
            position: "absolute",
            width: 220,
            height: 220,
            borderRadius: "50%",
            backgroundColor: "rgba(255,255,255,0.06)",
            top: -90,
            right: -70,
            pointerEvents: "none",
        },
        "&::after": {
            content: '""',
            position: "absolute",
            width: 140,
            height: 140,
            borderRadius: "50%",
            backgroundColor: "rgba(255,255,255,0.04)",
            bottom: -60,
            left: -30,
            pointerEvents: "none",
        },
    },
    bentoHeroLabel: {
        fontSize: "0.7rem !important",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        color: "rgba(255,255,255,0.7)",
        position: "relative",
        zIndex: 1,
    },
    bentoHeroNumber: {
        fontSize: "1.4rem !important",
        fontWeight: 800,
        lineHeight: 1,
        letterSpacing: "-0.03em",
        marginTop: theme.spacing(0.75),
        position: "relative",
        zIndex: 1,
    },
    bentoHeroSub: {
        fontSize: "0.78rem !important",
        color: "rgba(255,255,255,0.78)",
        fontWeight: 500,
        marginTop: theme.spacing(0.5),
        position: "relative",
        zIndex: 1,
    },
    bentoHeroFilterChip: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: "0.7rem !important",
        fontWeight: 700,
        backgroundColor: "rgba(255,255,255,0.14)",
        border: "1px solid rgba(255,255,255,0.18)",
        color: "white",
        backdropFilter: "blur(4px)",
        position: "relative",
        zIndex: 1,
        width: "fit-content",
    },
    bentoHeroChipDot: {
        width: 7,
        height: 7,
        borderRadius: 999,
        boxShadow: "0 0 0 2px rgba(255,255,255,0.18)",
    },
    bentoHeroFooter: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        justifyContent: "center",
        gap: theme.spacing(0.75),
        position: "relative",
        zIndex: 1,
        flexWrap: "wrap",
    },
    bentoHeroSparks: {
        display: "flex",
        gap: 4,
        alignItems: "flex-end",
    },
    bentoHeroSpark: {
        width: 6,
        borderRadius: 2,
        backgroundColor: "rgba(255,255,255,0.7)",
    },

    // Mini status cards (2x2 grid)
    bentoMini: {
        position: "relative",
        overflow: "hidden",
        padding: theme.spacing(0.9, 1.1),
        borderRadius: 14,
        border: `1px solid ${BORDER}`,
        backgroundColor: SURFACE,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: theme.spacing(0.55),
        // minHeight: 94,
        transition:
            "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background-color 180ms ease",
        "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 18px -10px rgba(15, 23, 42, 0.18)",
        },
        "&:focus-visible": {
            outline: "none",
        },
    },
    bentoMiniHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(0.75),
        minHeight: 30,
    },
    bentoMiniIconBubble: {
        width: 26,
        height: 26,
        borderRadius: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    bentoMiniLabel: {
        fontSize: "0.64rem !important",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        textAlign: "right",
        flex: 1,
        minWidth: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    bentoMiniBody: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(0.5),
        minHeight: 16,
    },
    bentoMiniCount: {
        fontWeight: 800,
        fontSize: "1.1rem !important",
        lineHeight: 1,
        letterSpacing: "-0.025em",
    },
    bentoMiniPct: {
        fontSize: "0.62rem !important",
        fontWeight: 700,
        color: TEXT_MUTED,
        lineHeight: 1.2,
    },
    bentoMiniBar: {
        height: 5,
        borderRadius: 999,
        backgroundColor: "#eef2f7",
        overflow: "hidden",
        width: "100%",
    },
    bentoMiniBarFill: {
        height: "100%",
        borderRadius: 999,
        transition: "width 240ms ease",
    },

    // ---------- Panels ----------
    panel: {
        backgroundColor: SURFACE,
        borderRadius: 12,
        border: `1px solid ${BORDER}`,
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
    },
    panelHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.5),
        minHeight: 48,
        borderBottom: `1px solid ${BORDER}`,
        flexShrink: 0,
        backgroundColor: "rgba(238, 245, 250, 0.8)",
    },
    panelHeaderLeft: {
        display: "flex",
        alignItems: "center",
        minWidth: 0,
    },
    panelBody: {
        padding: theme.spacing(1.5),
        flex: 1,
    },
    headerActions: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.75),
    },

    fieldLabel: {
        fontSize: "0.85rem !important",
        fontWeight: 600,
        color: TEXT_TITLE,
        // marginBottom: theme.spacing(0.75),
    },

    // ---------- Buttons ----------
    ghostBtn: {
        textTransform: "none",
        borderRadius: 8,
        fontWeight: 600,
        fontSize: "0.78rem !important",
        padding: theme.spacing(0.5, 1.25),
        color: TEXT_TITLE,
        border: `1px solid ${BORDER}`,
        backgroundColor: SURFACE,
        minHeight: 32,
        lineHeight: 1.2,
        "& .MuiButton-startIcon": {
            marginRight: 4,
        },
        "&:hover": {
            backgroundColor: "#f1f5f9",
            borderColor: "#cbd5e1",
        },
        "&.Mui-disabled": {
            opacity: 0.5,
        },
    },
    primaryBtn: {
        textTransform: "none",
        borderRadius: 8,
        fontWeight: 600,
        fontSize: "0.78rem !important",
        padding: theme.spacing(0.5, 1.5),
        backgroundColor: "#1d4ed8",
        color: "white",
        boxShadow: "0 1px 2px rgba(37, 99, 235, 0.25)",
        minHeight: 32,
        lineHeight: 1.2,
        "& .MuiButton-startIcon": {
            marginRight: 4,
        },
        "&:hover": {
            backgroundColor: ACCENT_DARK,
            boxShadow: "0 2px 6px rgba(37, 99, 235, 0.3)",
        },
    },

    // ---------- Table ----------
    tableCard: {
        borderRadius: 16,
        overflow: "hidden",
        marginTop: theme.spacing(1),
    },
    inputBase: {
        marginTop: "0.2rem",
        "& .MuiOutlinedInput-root": {
            borderRadius: 15,
            backgroundColor: "#f3f4f6",
            transition: "background-color 160ms ease, box-shadow 160ms ease",
            "& fieldset": { borderColor: "transparent" },
            "&:hover": { backgroundColor: "#eef2f7" },
            "&:hover fieldset": { borderColor: "transparent" },
            "&.Mui-focused": {
                backgroundColor: SURFACE,
                boxShadow: `0 0 0 3px ${ACCENT}1a`,
            },
            "&.Mui-focused fieldset": { borderColor: ACCENT, borderWidth: 1 },
        },
        "& .MuiOutlinedInput-input": {
            padding: "12px 12px !important",
            fontSize: "0.85rem !important",
            height: "1.25rem",
        },
        "& .MuiAutocomplete-inputRoot": {
            padding: "10px 12px !important",
        },
        "& .MuiAutocomplete-input": {
            padding: "6px 4px !important",
        },
        "& .MuiAutocomplete-endAdornment": {
            right: 8,
        },
    },

    autocompletePaper: {
        borderRadius: "12px",
        marginTop: "5px",
        background: "#ffffff",
        boxShadow: "0 8px 30px rgba(3, 46, 79, 0.18)",
        border: "1px solid rgba(11, 60, 93, 0.08)",
        "& .MuiList-root": {
            padding: "6px",
            background: "#ffffff",
            maxHeight: "200px",
            overflowY: "auto",
        },
        "& .MuiListItem-gutters": {
            paddingLeft: "5px",
            paddingRight: "5px",
        },
        "& .MuiListItem-root.Mui-focusVisible": {
            backgroundColor: "transparent",
        },
    },

    menuItem: {
        fontSize: "1rem",
        lineHeight: 1.5,
        minHeight: "auto",
        paddingTop: "0px",
        paddingBottom: "0px",
        "&.Mui-selected": {
            backgroundColor: "rgba(3, 169, 243, 0.12)",
            "&:hover": {
                backgroundColor: "rgba(3, 169, 243, 0.16)",
            },
        },
    },

    // ---------- Pie ----------
    // (removed unused pieBox)

    // ---------- Table ----------
    table: {
        width: "100%",
    },
    tableHeadRow: {
        backgroundColor: "rgba(241,245,249,0.8)",
    },
    tableHeadCell: {
        fontSize: "12px !important",
        color: "#64748B",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        fontWeight: 600,
        borderBottom: `1px solid ${BORDER}`,
    },
    tableBodyRow: {
        backgroundColor: SURFACE,
        "&:hover": {
            backgroundColor: "rgba(241,245,249,0.5)",
        },
        borderBottom: `1px solid ${BORDER}`,
    },
    tableBodyCell: {
        fontSize: "0.85rem !important",
        color: TEXT_TITLE,
        borderBottom: `1px solid ${BORDER}`,
    },
    tableToolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        padding: theme.spacing(1.25, 1.5),
        borderBottom: `1px solid ${BORDER}`,
        flexWrap: "wrap",
        backgroundColor: "white",
    },
    searchField: {
        minWidth: 180,
        flex: "1 1 280px",
        maxWidth: 260,
        "& .MuiOutlinedInput-root": {
            borderRadius: 15,
            backgroundColor: "#f3f4f6",
            transition: "background-color 160ms ease, box-shadow 160ms ease",
            "& fieldset": { borderColor: "transparent" },
            "&:hover": { backgroundColor: "#eef2f7" },
            "&:hover fieldset": { borderColor: "transparent" },
            "&.Mui-focused": {
                backgroundColor: SURFACE,
                boxShadow: `0 0 0 3px ${ACCENT}1a`,
            },
            "&.Mui-focused fieldset": { borderColor: ACCENT, borderWidth: 1 },
            marginTop: "0rem !important",
        },
        "& .MuiOutlinedInput-input": {
            padding: "12px 5px !important",
            fontSize: "0.85rem !important",
            height: "1.25rem",
        },
    },
    searchIcon: {
        color: TEXT_MUTED,
        fontSize: "18px !important",
    },
    tableToolbarActions: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(0.75),
        marginLeft: "auto",
    },
    statusChip: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px",
        borderRadius: 999,
        fontWeight: 600,
        fontSize: "0.80rem !important",
    },
    actionButton: {
        color: TEXT_MUTED,
        "&:hover": {
            color: ACCENT,
            backgroundColor: "rgba(37, 99, 235, 0.08)",
        },
    },
    dialogBtn: {
        margin: theme.spacing(1),
        borderRadius: 30,
        textTransform: "none",
    },
    emptyCell: {
        padding: "48px 0",
        color: TEXT_MUTED,
        textAlign: "center",
    },
    footer: {
        padding: "12px 24px",
        borderTop: `1px solid ${BORDER}`,
        backgroundColor: "rgba(241,245,249,0.3)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    footerLeft: {
        display: "flex",
        alignItems: "center",
    },
    footerText: {
        color: TEXT_MUTED,
        marginRight: 8,
    },
    rowsSelect: {
        "& .MuiOutlinedInput-root": {
            height: 32,
        },
        "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
            borderRadius: 8,
        },
        "& .MuiSelect-select": {
            padding: "5px 25px 5px 10px",
        },
    },
    footerRight: {
        display: "flex",
        alignItems: "center",
    },
    pageText: {
        color: TEXT_MUTED,
        marginRight: 12,
    },

    // ---------- Dialog Styles ----------
    dialogContentMain: {
        fontWeight: 500,
        fontSize: "14px !important",
        color: TEXT_TITLE,
        lineHeight: 1.6,
    },
    dialogContentSub: {
        fontWeight: 400,
        fontSize: "12px !important",
        color: TEXT_MUTED,
        lineHeight: 1.5,
    },
    dialogHeader: {
        padding: "14px 20px",
        borderBottom: "1px solid #f1f5f9",
        display: "flex",
        alignItems: "center",
        gap: 10,
        backgroundColor: "#FFF5F5",
    },
    dialogHeaderIconBox: {
        width: 40,
        height: 40,
        borderRadius: 8,
        backgroundColor: "#fee2e2",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    dialogHeaderTitle: {
        fontWeight: 700,
        fontSize: "16px !important",
        color: "#991b1b",
        textTransform: "uppercase",
        letterSpacing: 0.8,
    },
    dialogBody: {
        padding: "18px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
    },
    dialogFooter: {
        padding: "12px 20px",
        borderTop: "1px solid #e2e8f0",
        gap: 10,
    },
    dialogBtnOutlined: {
        borderRadius: 8,
        fontWeight: 600,
        color: TEXT_TITLE,
        borderColor: BORDER,
        textTransform: "none",
        padding: "8px 20px",
        fontSize: "13px !important",
    },
    dialogBtnCancel: {
        borderRadius: 8,
        fontWeight: 600,
        background: "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)",
        textTransform: "none",
        padding: "8px 20px",
        fontSize: "13px !important",
        boxShadow: "0 2px 8px rgba(220, 38, 38, 0.35)",
    },
}));

const InspectionJobManagement = (props) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const userRole = useSelector((state) => state.OAuth.authData.role || "");
    const [makeSelected] = useContext(Context);

    const [totalDataSet, setTotalDataSet] = useState([]);
    const [dataSet, setDataSet] = useState([]);
    const [statusData, setStatusData] = useState({});
    const [activeIndex, setActiveIndex] = useState(null);

    const [model, setModel] = useState("");
    const [modelData, setModelData] = useState([]);
    const [market, setMarket] = useState("");
    const [marketData, setMarketData] = useState([]);
    const [year, setYear] = useState("");
    const [yearData, setYearData] = useState([]);
    const [plant, setPlant] = useState("");
    const [plantData, setPlantData] = useState([]);
    const [statusArray, setStatusArray] = useState([]);
    const [technician, setTechnician] = useState("");
    const [technicianData, setTechnicianData] = useState([]);
    const [jobId, setJobId] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [page, setPage] = useState(1);
    const [tableSearch, setTableSearch] = useState("");
    const [exportAnchorEl, setExportAnchorEl] = useState(null);

    const [useEffectRecall, setUseEffectRecall] = useState(false);
    const [openCancelDialog, setOpenCancelDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});

    const formatPublicationType = (rowData) => {
        const pubType = rowData.publicationType || "-";
        const languageName = rowData.languageName || "-";
        const langCode =
            languageName !== "-" ? languageName.slice(-2).toUpperCase() : "-";
        if (langCode !== "-" && pubType !== "-") return `${pubType}(${langCode})`;
        if (pubType !== "-") return `${pubType}(-)`;
        return "-";
    };

    const decorateRows = (rows) =>
        (rows || []).map((d) => ({
            ...d,
            inspectionDate: moment(d.inspectionDate).format("DD/MM/YYYY"),
            makeId: makeSelected && makeSelected.uid,
            techniciannames:
                d?.technicians?.length > 0 ? d.technicians.join() : "",
            publicationTypeFormatted: formatPublicationType(d),
        }));

    const computeStatusCounts = (rows) => ({
        New: rows.filter((m) => m.status === "NEW").length,
        Inprogress: rows.filter((m) => m.status === "IN_PROGRESS").length,
        Completed: rows.filter((m) => m.status === "COMPLETED").length,
        Cancelled: rows.filter((m) => m.status === "CANCELLED").length,
    });

    // ---------- Table columns ----------
    const renderStatusBadge = (rowData) => {
        const palette = STATUS_PALETTE[rowData.status];
        if (!palette) return <span style={{ color: TEXT_MUTED }}>N/A</span>;
        const StatusIcon = palette.legacyIcon;
        return (
            <span
                className={classes.statusChip}
                style={{
                    backgroundColor: palette.soft,
                    color: palette.main,
                    border: `1px solid ${palette.border}`,
                }}
            >
                <StatusIcon style={{ fontSize: 14 }} />
                {palette.label}
            </span>
        );
    };

    const columns = [
        { title: "Model", field: "modelName", editable: "never", cellStyle: { width: 120 } },
        { title: "Market", field: "market", editable: "never" },
        { title: "Year", field: "year", editable: "never" },
        {
            title: "Inspection Date",
            field: "inspectionDate",
            editable: "never",
            cellStyle: { width: 120 },
        },
        {
            title: "Technician",
            field: "techniciannames",
        },
        {
            title: "Publication Type",
            field: "publicationTypeFormatted",
            editable: "never",
            render: (rowData) => formatPublicationType(rowData),
        },
        { title: "Plant Details", field: "plantDetails", editable: "never" },
        {
            title: "Status",
            field: "status",
            render: renderStatusBadge,
            editComponent: (rowData) => (
                <Select defaultValue={rowData.rowData.status}>
                    <MenuItem value={1}>Present</MenuItem>
                    <MenuItem value={0}>Absent</MenuItem>
                </Select>
            ),
        },
        {
            title: "Inspection Progress",
            field: "inspectionProgress",
            editable: "never",
            cellStyle: { width: 140 },
            render: (rowData) => (
                <CustomProgressBar
                    progress={rowData.inspectionProgress}
                    status={rowData.status}
                />
            ),
        },
    ];

    const tableIcons = {
        file: React.forwardRef((iconProps, ref) => (
            <DescriptionOutlinedIcon {...iconProps} ref={ref} />
        )),
    };

    const actions = [
        {
            icon: tableIcons.file,
            tooltip: "View Job Details",
            onClick: (event, rowData) => {
                if (
                    props.history &&
                    window._appConstants &&
                    window._appConstants.useCommonDefaults
                ) {
                    props.history.push({
                        pathname: window._appConstants.useCommonDefaults.navRef.jobDetails,
                        rowdata: rowData,
                    });
                }
            },
        },
        (rowData) =>
            userRole.toUpperCase() === "SUPER_ADMIN" || userRole.toUpperCase() === "ADMIN"
                ? rowData.status !== "CANCELLED" && rowData.status !== "Cancelled"
                    ? {
                        icon: SwapHorizIcon,
                        tooltip: "Reassign Or Transfer Job",
                        onClick: (event, rowData) => {
                            if (
                                props.history &&
                                window._appConstants &&
                                window._appConstants.useCommonDefaults
                            ) {
                                props.history.push({
                                    pathname:
                                        window._appConstants.useCommonDefaults.navRef.inspection,
                                    rowdata: rowData,
                                });
                            }
                        },
                    }
                    : null
                : null,
        (rowData) =>
            userRole.toUpperCase() === "SUPER_ADMIN" || userRole.toUpperCase() === "ADMIN"
                ? rowData.status !== "CANCELLED" && rowData.status !== "Cancelled"
                    ? {
                        icon: HighlightOffOutlinedIcon,
                        tooltip: "Cancel Job",
                        onClick: (event, rowData) => {
                            setSelectedRow(rowData);
                            setOpenCancelDialog(true);
                        },
                    }
                    : null
                : null,
    ];

    // ---------- Filters by status (cards + pie) ----------
    const statusKeyFromIndex = (index) =>
        index === 0
            ? "NEW"
            : index === 1
                ? "IN_PROGRESS"
                : index === 2
                    ? "COMPLETED"
                    : "CANCELLED";

    const filterByStatusCard = (index) => {
        if (activeIndex !== index) {
            const key = statusKeyFromIndex(index);
            setActiveIndex(index);
            setDataSet(_.cloneDeep(totalDataSet).filter((m) => m.status === key));
        } else {
            clearStatusFilter();
        }
    };

    const clearStatusFilter = () => {
        setActiveIndex(null);
        setDataSet(_.cloneDeep(totalDataSet));
    };

    const statusLabelByKey = (status) =>
        STATUS_PALETTE[status] ? STATUS_PALETTE[status].label : status || "";

    const getExportValue = (rowData, col) => {
        if (col.field === "publicationTypeFormatted") {
            return formatPublicationType(rowData);
        }
        if (col.field === "status") {
            return statusLabelByKey(rowData.status);
        }
        return rowData[col.field] || "-";
    };

    const downloadBlob = (content, fileName, mimeType) => {
        const blob = new Blob([content], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);
    };

    const exportRows = (rows) => {
        const exportColumns = columns.filter((col) => col.field);
        return rows.map((row) => {
            const line = {};
            exportColumns.forEach((col) => {
                line[col.title] = getExportValue(row, col);
            });
            return line;
        });
    };

    const handleExportCsv = () => {
        const rows = exportRows(filteredDataSet);
        const headers = rows.length > 0 ? Object.keys(rows[0]) : columns.map((col) => col.title);
        const csvRows = [headers.join(",")];
        rows.forEach((row) => {
            const values = headers.map((header) => {
                const value = row[header] === null || row[header] === undefined ? "" : String(row[header]);
                return `"${value.replace(/"/g, '""')}"`;
            });
            csvRows.push(values.join(","));
        });
        downloadBlob(
            csvRows.join("\n"),
            `inspection-job-details-${moment().format("YYYYMMDD-HHmmss")}.csv`,
            "text/csv;charset=utf-8;"
        );
    };

    const handleExportJson = () => {
        const rows = exportRows(filteredDataSet);
        downloadBlob(
            JSON.stringify(rows, null, 2),
            `inspection-job-details-${moment().format("YYYYMMDD-HHmmss")}.json`,
            "application/json;charset=utf-8;"
        );
    };

    // ---------- Data fetching ----------
    const loadFilterDropdowns = async () => {
        let roleID = "";
        await dispatch(OAuthActions.getRole()).then((roles) => {
            if (roles) {
                const technicianRole = roles.find((c) => c.name === "Technician");
                if (technicianRole) roleID = technicianRole.uid;
            }
        });

        await dispatch(OAuthActions.getAllModel(makeSelected.uid)).then((data) => {
            if (data) setModelData(data);
        });
        await dispatch(
            OAuthActions.getData(buildApiUrl("/vehicle/market/all"))
        ).then((data) => {
            if (data) setMarketData(data);
        });
        await dispatch(
            OAuthActions.getData(buildApiUrl("/vehicle/year/all"))
        ).then((data) => {
            if (data) setYearData(data);
        });
        await dispatch(OAuthActions.getPlants(makeSelected.uid)).then((pltData) => {
            if (pltData) {
                setPlantData(
                    pltData?._embedded?.plantLists === undefined
                        ? []
                        : pltData?._embedded?.plantLists
                );
            }
        });
        if (roleID) {
            await dispatch(OAuthActions.getUsers(makeSelected.uid, roleID)).then(
                (tchnData) => {
                    if (tchnData) {
                        setTechnicianData(
                            tchnData?._embedded?.userLists === undefined
                                ? []
                                : tchnData?._embedded?.userLists
                        );
                    }
                }
            );
        }
    };

    const loadJobs = async (filters = {}) => {
        await dispatch(
            OAuthActions.getReportsData(makeSelected.uid, filters)
        ).then((data) => {
            let totalData = [];
            if (data && data._embedded && data._embedded.jobBasicDetailsDtoes) {
                totalData = decorateRows(data._embedded.jobBasicDetailsDtoes);
            }
            setTotalDataSet(totalData);
            setDataSet(totalData);
            setStatusData(computeStatusCounts(totalData));
            setActiveIndex(null);
        });
    };

    useEffect(() => {
        if (!makeSelected || !makeSelected.uid) {
            return;
        }
        const init = async () => {
            await loadJobs({
                technicianId: "",
                plantId: "",
                model_Name: "",
                marketId: "",
                jobStatus: "",
                yearId: "",
            });
            await loadFilterDropdowns();
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [makeSelected, useEffectRecall]);

    const handleSearch = async () => {
        if (!jobId) {
            const status = statusArray
                .map((s) => s.id)
                .filter(Boolean)
                .join();
            const filters = {
                technicianId: technician ? technician.uid : "",
                plantId: plant ? plant.uid : "",
                model_Name: model ? model.modelName : "",
                marketId: market ? market.uid : "",
                jobStatus: status || "",
                yearId: year ? year.uid : "",
            };
            setPage(1);
            await loadJobs(filters);
        } else {
            await handleResetFilters(false, true);
            await dispatch(OAuthActions.getBasicDetailsbyJobid(jobId)).then(
                (data) => {
                    let totalData = [];
                    if (data && data["error"] === undefined) {
                        totalData = decorateRows([data]);
                    }
                    setPage(1);
                    setTotalDataSet(totalData);
                    setDataSet(totalData);
                    setStatusData(computeStatusCounts(totalData));
                    setActiveIndex(null);
                    if (data && data["error"] !== undefined) {
                        dispatch(
                            setAlertDialog(
                                true,
                                null,
                                "error",
                                "Job Id Search",
                                "Job Id search unsuccessful.\nDetails Of Failure: " +
                                data.message,
                                "OK",
                                ""
                            )
                        );
                    }
                }
            );
        }
    };

    const handleResetFilters = async (reload = true, keepJobId = false) => {
        setModel("");
        setMarket("");
        setYear("");
        setPlant("");
        setStatusArray([]);
        setTechnician("");
        if (!keepJobId) {
            setJobId("");
        }
        setPage(1);
        if (reload) {
            await loadJobs({
                technicianId: "",
                plantId: "",
                model_Name: "",
                marketId: "",
                jobStatus: "",
                yearId: "",
            });
        }
    };

    // ---------- Pagination logic ----------
    const ROWS_OPTIONS = [5, 10, 20];
    const normalizedTableSearch = tableSearch.trim().toLowerCase();
    const filteredDataSet = normalizedTableSearch
        ? dataSet.filter((row) => {
            const rowValues = [
                row.jobId,
                row.modelName,
                row.market,
                row.year,
                row.inspectionDate,
                row.techniciannames,
                formatPublicationType(row),
                row.plantDetails,
                statusLabelByKey(row.status),
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return rowValues.includes(normalizedTableSearch);
        })
        : dataSet;

    const totalPages = Math.max(1, Math.ceil(filteredDataSet.length / rowsPerPage));
    const paginatedData = filteredDataSet.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    const handleRowsPerPage = (event) => {
        setRowsPerPage(Number(event.target.value));
        setPage(1);
    };

    useEffect(() => {
        setPage(1);
    }, [tableSearch]);

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const customTableData = {
        isReadOnly: true,
        isOnclick: false,
        dataSet,
        columns,
        actions,
        exportOption: true,
        exportOptionPdf: true,
        searchState: true,
        exportFileName: "Inspection Job Details",
    };

    // ---------- Render: bento mini-card for the Overview grid ----------
    const totalJobs =
        (statusData.New || 0) +
        (statusData.Inprogress || 0) +
        (statusData.Completed || 0) +
        (statusData.Cancelled || 0);

    const renderBentoMini = (statusKey, index, gridArea) => {
        const palette = STATUS_PALETTE[statusKey];
        const { Icon, label, main, soft, border } = palette;
        const countKey =
            statusKey === "NEW"
                ? "New"
                : statusKey === "IN_PROGRESS"
                    ? "Inprogress"
                    : statusKey === "COMPLETED"
                        ? "Completed"
                        : "Cancelled";
        const isActive = activeIndex === index;
        const count = statusData[countKey] || 0;
        const sharePct = totalJobs ? Math.round((count / totalJobs) * 100) : 0;

        return (
            <Box
                key={statusKey}
                className={classes.bentoMini}
                style={{
                    gridArea,
                    backgroundColor: isActive ? soft : SURFACE,
                    borderColor: isActive ? main : BORDER,
                    boxShadow: isActive
                        ? `0 0 0 2px ${main}26, 0 8px 18px -10px ${main}55`
                        : "0 1px 2px rgba(15, 23, 42, 0.04)",
                }}
                onClick={() => filterByStatusCard(index)}
                role="button"
                tabIndex={0}
                aria-pressed={isActive}
            >
                <Box className={classes.bentoMiniHeader}>
                    <Box
                        className={classes.bentoMiniIconBubble}
                        style={{
                            backgroundColor: soft,
                            color: main,
                            border: `1px solid ${border}`,
                        }}
                    >
                        <Icon style={{ fontSize: 16 }} />
                    </Box>

                    <Typography
                        className={classes.bentoMiniCount}
                        style={{ color: main }}
                    >
                        {count}
                    </Typography>

                    <Typography
                        className={classes.bentoMiniLabel}
                        style={{ color: main }}
                    >
                        {label}
                    </Typography>
                </Box>

                <Box className={classes.bentoMiniBody}>
                    <Box className={classes.bentoMiniBar}>
                        <Box
                            className={classes.bentoMiniBarFill}
                            style={{
                                width: `${sharePct}%`,
                                backgroundColor: main,
                            }}
                        />
                    </Box>
                    <Typography className={classes.bentoMiniPct}>
                        {sharePct}%
                    </Typography>
                </Box>
            </Box>
        );
    };

    // Sparkline-style mini bars for the hero card (proportions of each status)
    // const renderHeroSparks = () => {
    //     const buckets = [
    //         { key: "NEW", value: statusData.New || 0 },
    //         { key: "IN_PROGRESS", value: statusData.Inprogress || 0 },
    //         { key: "COMPLETED", value: statusData.Completed || 0 },
    //         { key: "CANCELLED", value: statusData.Cancelled || 0 },
    //     ];
    //     const max = Math.max(1, ...buckets.map((b) => b.value));
    //     return (
    //         <Box className={classes.bentoHeroSparks}>
    //             {buckets.map((b) => (
    //                 <Box
    //                     key={b.key}
    //                     className={classes.bentoHeroSpark}
    //                     style={{
    //                         height: 18 + (b.value / max) * 26,
    //                         opacity: b.value === 0 ? 0.3 : 0.85,
    //                     }}
    //                 />
    //             ))}
    //         </Box>
    //     );
    // };

    const renderField = (label, control) => (
        <Grid item xs={12} sm={6} lg={4}>
            <Typography className={classes.fieldLabel}>{label}</Typography>
            {control}
        </Grid>
    );

    return (
        <div className={classes.page}>
            <Box className={classes.stack}>
                <Box className={classes.pageHeaderLeft}>
                    <Box className={classes.headerIcon}>
                        <img src={chartSvg} alt="dashboard" style={{ width: 22, height: 22, filter: "brightness(0) invert(1)" }} />
                    </Box>

                    <Typography >
                        <strong style={{ fontSize: "1.4rem" }}>Inspection Job Management</strong>
                    </Typography>
                </Box>

                {/* Status Overview (left) + Filters (right) */}
                <Grid container spacing={2} alignItems="stretch">
                    <Grid item xs={12} md={5}>
                        <Box className={classes.panel} style={{ height: "100%" }}>
                            <Box className={classes.panelHeader}>
                                <Box className={classes.panelHeaderLeft}>
                                    <Box className={classes.pageIconBox}>
                                        <DonutLargeIcon className={classes.pageTitleIcon} />
                                    </Box>
                                    <Typography className={classes.moreDetailsTitle}>
                                        Job Status Overview
                                    </Typography>
                                </Box>
                                <Button
                                    className={classes.ghostBtn}
                                    onClick={clearStatusFilter}
                                    disabled={activeIndex === null}
                                >
                                    Clear Filter
                                </Button>
                            </Box>
                            <Box className={classes.overviewBody}>
                                <Box className={classes.bentoGrid}>
                                    {/* Hero card — Total Jobs */}
                                    <Box className={classes.bentoHero}>
                                        <Box>
                                            <Typography className={classes.bentoHeroLabel}>
                                                Total Jobs
                                            </Typography>
                                            <Typography className={classes.bentoHeroNumber}>
                                                {totalJobs}
                                            </Typography>
                                            <Typography className={classes.bentoHeroSub}>
                                                {activeIndex === null
                                                    ? "Tracking all 4 inspection statuses"
                                                    : `Filtered view applied`}
                                            </Typography>
                                        </Box>

                                        <Box className={classes.bentoHeroFooter}>
                                            {activeIndex !== null ? (
                                                <Box
                                                    className={classes.bentoHeroFilterChip}
                                                    role="button"
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <Box
                                                        className={classes.bentoHeroChipDot}
                                                        style={{
                                                            backgroundColor:
                                                                STATUS_PALETTE[statusKeyFromIndex(activeIndex)]
                                                                    .main,
                                                        }}
                                                    />
                                                    <span>
                                                        {
                                                            STATUS_PALETTE[statusKeyFromIndex(activeIndex)]
                                                                .label
                                                        }
                                                    </span>
                                                    <span style={{ opacity: 0.7 }}>· clear</span>
                                                </Box>
                                            ) : (
                                                <Box className={classes.bentoHeroFilterChip}>
                                                    <span style={{ opacity: 0.85 }}>No filter active</span>
                                                </Box>
                                            )}
                                            {/* {renderHeroSparks()} */}
                                        </Box>
                                    </Box>

                                    {/* 2×2 mini-cards */}
                                    {renderBentoMini("NEW", 0, "new")}
                                    {renderBentoMini("IN_PROGRESS", 1, "inprog")}
                                    {renderBentoMini("COMPLETED", 2, "comp")}
                                    {renderBentoMini("CANCELLED", 3, "cancel")}
                                </Box>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={7}>
                        <Box className={classes.panel} style={{ height: "100%" }}>
                            <Box className={classes.panelHeader}>
                                <Box className={classes.panelHeaderLeft}>
                                    <Box className={classes.pageIconBox}>
                                        <TuneIcon className={classes.pageTitleIcon} />
                                    </Box>
                                    <Typography className={classes.moreDetailsTitle}>
                                        Filters
                                    </Typography>
                                </Box>
                                <Box className={classes.headerActions}>
                                    <Button
                                        className={classes.ghostBtn}
                                        startIcon={<RefreshIcon style={{ fontSize: 14 }} />}
                                        onClick={() => handleResetFilters(true)}
                                    >
                                        Reset
                                    </Button>

                                </Box>
                            </Box>
                            <Box className={classes.panelBody}>
                                <Grid container spacing={1}>
                                    {renderField(
                                        <Autocomplete
                                            value={model || null}
                                            options={modelData}
                                            getOptionLabel={(option) => option.modelName || ""}
                                            ListboxProps={{ style: { maxHeight: 200 } }}
                                            onChange={(e, v) => setModel(v ? v : "")}
                                            PaperComponent={({ children }) => (
                                                <div
                                                    className={classes.autocompletePaper}
                                                    style={{ maxHeight: 200, overflow: "hidden" }}
                                                >
                                                    {children}
                                                </div>
                                            )}
                                            renderOption={(option) => (
                                                <MenuItem
                                                    className={classes.menuItem}
                                                    key={option.uid}
                                                    value={option.uid}
                                                    style={{
                                                        whiteSpace: "normal",
                                                        wordBreak: "break-word",
                                                        display: "block",
                                                    }}
                                                >
                                                    {option.modelName}
                                                </MenuItem>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    placeholder="Model"
                                                    className={classes.inputBase}
                                                />
                                            )}
                                        />
                                    )}
                                    {renderField(
                                        <Autocomplete
                                            value={market || null}
                                            options={marketData}
                                            getOptionLabel={(option) => option.name || ""}
                                            ListboxProps={{ style: { maxHeight: 200 } }}
                                            onChange={(e, v) => setMarket(v ? v : "")}
                                            PaperComponent={({ children }) => (
                                                <div
                                                    className={classes.autocompletePaper}
                                                    style={{ maxHeight: 200, overflow: "hidden" }}
                                                >
                                                    {children}
                                                </div>
                                            )}
                                            renderOption={(option) => (
                                                <MenuItem
                                                    className={classes.menuItem}
                                                    key={option.uid}
                                                    value={option.uid}
                                                    style={{
                                                        whiteSpace: "normal",
                                                        wordBreak: "break-word",
                                                        display: "block",
                                                    }}
                                                >
                                                    {option.name}
                                                </MenuItem>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    placeholder="Market"
                                                    className={classes.inputBase}
                                                />
                                            )}
                                        />
                                    )}
                                    {renderField(
                                        <Autocomplete
                                            value={year || null}
                                            options={yearData}
                                            ListboxProps={{ style: { maxHeight: 200 } }}
                                            getOptionLabel={(option) =>
                                                option.year ? `${option.year}` : ""
                                            }
                                            onChange={(e, v) => setYear(v ? v : "")}
                                            PaperComponent={({ children }) => (
                                                <div
                                                    className={classes.autocompletePaper}
                                                    style={{ maxHeight: 200, overflow: "hidden" }}
                                                >
                                                    {children}
                                                </div>
                                            )}
                                            renderOption={(option) => (
                                                <MenuItem
                                                    className={classes.menuItem}
                                                    key={option.uid}
                                                    value={option.uid}
                                                    style={{
                                                        whiteSpace: "normal",
                                                        wordBreak: "break-word",
                                                        display: "block",
                                                    }}
                                                >
                                                    {option.year}
                                                </MenuItem>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    placeholder="Year"
                                                    className={classes.inputBase}
                                                />
                                            )}
                                        />
                                    )}
                                    {renderField(
                                        <Autocomplete
                                            value={plant || null}
                                            options={plantData}
                                            getOptionLabel={(option) => option.plantName || ""}
                                            ListboxProps={{ style: { maxHeight: 200 } }}
                                            onChange={(e, v) => setPlant(v ? v : "")}
                                            PaperComponent={({ children }) => (
                                                <div
                                                    className={classes.autocompletePaper}
                                                    style={{ maxHeight: 200, overflow: "hidden" }}
                                                >
                                                    {children}
                                                </div>
                                            )}
                                            renderOption={(option) => (
                                                <MenuItem
                                                    className={classes.menuItem}
                                                    key={option.uid}
                                                    value={option.uid}
                                                    style={{
                                                        whiteSpace: "normal",
                                                        wordBreak: "break-word",
                                                        display: "block",
                                                    }}
                                                >
                                                    {option.plantName}
                                                </MenuItem>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    placeholder="Plant"
                                                    className={classes.inputBase}
                                                />
                                            )}
                                        />
                                    )}
                                    {renderField(
                                        <Autocomplete
                                            multiple
                                            value={statusArray}
                                            ListboxProps={{ style: { maxHeight: 200 } }}
                                            options={
                                                (window._appConstants &&
                                                    window._appConstants.statusDataInspection) ||
                                                []
                                            }
                                            getOptionLabel={(option) => option.label || ""}
                                            onChange={(event, newValue) => {
                                                const dedup = newValue.reduce((acc, cur) => {
                                                    if (!acc.some((x) => x.label === cur.label)) {
                                                        acc.push(cur);
                                                    }
                                                    return acc;
                                                }, []);
                                                setStatusArray([...dedup]);
                                            }}
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => (
                                                    <Chip
                                                        key={option.label}
                                                        label={`${option.label}`}
                                                        size="small"
                                                        {...getTagProps({ index })}
                                                    />
                                                ))
                                            }
                                            PaperComponent={({ children }) => (
                                                <div
                                                    className={classes.autocompletePaper}
                                                    style={{ maxHeight: 200, overflow: "hidden" }}
                                                >
                                                    {children}
                                                </div>
                                            )}
                                            renderOption={(option) => (
                                                <MenuItem
                                                    className={classes.menuItem}
                                                    key={option.label}
                                                    value={option.label}
                                                    style={{
                                                        whiteSpace: "normal",
                                                        wordBreak: "break-word",
                                                        display: "block",
                                                    }}
                                                >
                                                    {option.label}
                                                </MenuItem>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    placeholder={statusArray.length === 0 ? "Status" : ""}
                                                    className={classes.inputBase}
                                                />
                                            )}
                                        />
                                    )}
                                    {renderField(
                                        <Autocomplete
                                            value={technician || null}
                                            options={technicianData}
                                            ListboxProps={{ style: { maxHeight: 200 } }}
                                            getOptionLabel={(option) =>
                                                option && option.firstName
                                                    ? `${option.firstName} ${option.lastName}`
                                                    : ""
                                            }
                                            onChange={(e, v) => setTechnician(v ? v : "")}
                                            PaperComponent={({ children }) => (
                                                <div
                                                    className={classes.autocompletePaper}
                                                    style={{ maxHeight: 200, overflow: "hidden" }}
                                                >
                                                    {children}
                                                </div>
                                            )}
                                            renderOption={(option) => (
                                                <MenuItem
                                                    className={classes.menuItem}
                                                    key={option.uid}
                                                    value={option.uid}
                                                    style={{
                                                        whiteSpace: "normal",
                                                        wordBreak: "break-word",
                                                        display: "block",
                                                    }}
                                                >
                                                    {option && option.firstName
                                                        ? `${option.firstName} ${option.lastName}`
                                                        : ""}
                                                </MenuItem>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    placeholder="Technician"
                                                    className={classes.inputBase}
                                                />
                                            )}
                                        />
                                    )}
                                    {renderField(
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            placeholder="Enter Job ID"
                                            value={jobId}
                                            onChange={(event) => setJobId(event.target.value)}
                                            className={classes.inputBase}
                                        />
                                    )}
                                </Grid>
                                <Box style={{ display: "flex", justifyContent: "flex-end" }}>
                                    <Button
                                        className={classes.primaryBtn}
                                        startIcon={<SearchIcon style={{ fontSize: 14 }} />}
                                        onClick={handleSearch}
                                        disableElevation
                                    >
                                        Search
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                {/* Table */}
                <Paper variant="outlined" className={classes.tableCard}>
                    <Box className={classes.tableToolbar}>
                        <TextField
                            size="small"
                            variant="outlined"
                            value={tableSearch}
                            onChange={(event) => setTableSearch(event.target.value)}
                            placeholder="Search"
                            className={classes.searchField}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon className={classes.searchIcon} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Box className={classes.tableToolbarActions}>
                            <Button
                                className={classes.primaryBtn}
                                onClick={(e) => setExportAnchorEl(e.currentTarget)}
                            >
                                Export
                            </Button>
                            <Menu
                                anchorEl={exportAnchorEl}
                                open={Boolean(exportAnchorEl)}
                                onClose={() => setExportAnchorEl(null)}
                                PaperProps={{ style: { borderRadius: 12 } }}
                            >
                                <MenuItem
                                    onClick={() => {
                                        handleExportCsv();
                                        setExportAnchorEl(null);
                                    }}
                                >
                                    CSV
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        handleExportJson();
                                        setExportAnchorEl(null);
                                    }}
                                >
                                    JSON
                                </MenuItem>
                            </Menu>
                        </Box>
                    </Box>
                    <TableContainer>
                        <Table className={classes.table}>
                            <TableHead>
                                <TableRow className={classes.tableHeadRow}>
                                    {columns.map((col) => (
                                        <TableCell
                                            key={col.field}
                                            className={classes.tableHeadCell}
                                            style={col.cellStyle}
                                        >
                                            {col.title}
                                        </TableCell>
                                    ))}
                                    {actions && actions.length > 0 && (
                                        <TableCell className={classes.tableHeadCell}>
                                            Actions
                                        </TableCell>
                                    )}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginatedData && paginatedData.length > 0 ? (
                                    paginatedData.map((rowData, rowIndex) => (
                                        <TableRow
                                            key={rowData.jobId || rowIndex}
                                            className={classes.tableBodyRow}
                                        >
                                            {columns.map((col) => (
                                                <TableCell
                                                    key={`${rowIndex}-${col.field}`}
                                                    className={classes.tableBodyCell}
                                                    style={col.cellStyle}
                                                >
                                                    {col.render
                                                        ? col.render(rowData)
                                                        : rowData[col.field] || "-"}
                                                </TableCell>
                                            ))}
                                            {actions && actions.length > 0 && (
                                                <TableCell className={classes.tableBodyCell}>
                                                    <Box display="flex" gap={0.5}>
                                                        {actions.map((action, actionIndex) => {
                                                            if (typeof action === "function") {
                                                                const actionObj = action(rowData);
                                                                if (!actionObj) return null;
                                                                const Icon = actionObj.icon;
                                                                return (
                                                                    <Tooltip
                                                                        key={actionIndex}
                                                                        title={actionObj.tooltip || ""}
                                                                        arrow
                                                                    >
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(event) =>
                                                                                actionObj.onClick(event, rowData)
                                                                            }
                                                                            className={classes.actionButton}
                                                                        >
                                                                            <Icon style={{ fontSize: 25, color: "#1E3A8A" }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                );
                                                            } else if (action && action.icon) {
                                                                const Icon = action.icon;
                                                                return (
                                                                    <Tooltip
                                                                        key={actionIndex}
                                                                        title={action.tooltip || ""}
                                                                        arrow
                                                                    >
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(event) =>
                                                                                action.onClick(event, rowData)
                                                                            }
                                                                            className={classes.actionButton}
                                                                        >
                                                                            <Icon style={{ fontSize: 25, color: "red" }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                );
                                                            }
                                                            return null;
                                                        })}
                                                    </Box>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell
                                            colSpan={columns.length + (actions?.length > 0 ? 1 : 0)}
                                            className={classes.emptyCell}
                                        >
                                            No data available
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination Footer */}
                    <Box className={classes.footer}>
                        <Box className={classes.footerLeft}>
                            <Typography variant="body2" className={classes.footerText}>
                                Rows per page:
                            </Typography>

                            <Select
                                style={{ borderRadius: 8 }}
                                size="small"
                                variant="outlined"
                                value={rowsPerPage}
                                onChange={handleRowsPerPage}
                                className={classes.rowsSelect}
                            >
                                {ROWS_OPTIONS.map((n) => (
                                    <MenuItem key={n} value={n}>
                                        {n}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>

                        <Box className={classes.footerRight}>
                            <Typography variant="body2" className={classes.pageText}>
                                Page {page} of {totalPages}
                            </Typography>

                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, value) => setPage(value)}
                                shape="circular"
                                size="small"
                            />
                        </Box>
                    </Box>
                </Paper>
            </Box>

            {/* Cancel-job confirmation dialog */}
            <Dialog
                open={openCancelDialog}
                keepMounted
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    style: {
                        borderRadius: 15,
                        boxShadow: "0 1px 4px rgba(0,0,0,0.06), 0 4px 20px rgba(0,0,0,0.05)",
                    },
                }}
            >
                <Box className={classes.dialogHeader}>
                    <Box className={classes.dialogHeaderIconBox}>
                        <HighlightOffOutlinedIcon style={{ fontSize: 22, color: "#dc2626" }} />
                    </Box>
                    <Typography className={classes.dialogHeaderTitle}>
                        Cancel Job
                    </Typography>
                </Box>

                <DialogContent className={classes.dialogBody}>
                    <Typography className={classes.dialogContentMain}>
                        Are you sure you want to cancel this inspection job?
                    </Typography>
                    <Typography className={classes.dialogContentSub}>
                        Once cancelled, this action cannot be undone. The job status will be permanently marked as cancelled.
                    </Typography>
                </DialogContent>

                <DialogActions className={classes.dialogFooter}>
                    <Button
                        onClick={() => setOpenCancelDialog(false)}
                        variant="outlined"
                        className={classes.dialogBtnOutlined}
                    >
                        Keep
                    </Button>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            setOpenCancelDialog(false);
                            await dispatch(
                                OAuthActions.cancelUpdateJob(selectedRow.jobId, {
                                    status: "CANCELLED",
                                })
                            ).then((data) => {
                                if (data && data.status === 200) {
                                    dispatch(
                                        setSnackbar(true, "success", "Job Cancelled Successfully")
                                    );
                                    setUseEffectRecall((prev) => !prev);
                                }
                            });
                        }}
                        className={classes.dialogBtnCancel}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default InspectionJobManagement;
